import './HomePage.scss'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Header from '../../components/Header/Header'
import { AdminContentApi } from '../../services/api/adminContent'
import { QuestionsApi } from '../../services/api/questions'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { getApiErrorMessage } from '../../utils/api/getApiErrorMessage'
import { canManageContent, isAdminRole } from '../../utils/roles'
import { DirectionResponse, QuestionResponse, QuestionUpsertRequest, TagResponse } from '../../types/types'
import { renderMarkdown } from '../../utils/markdown/renderMarkdown'

const TEXT = {
    title: 'Вопросы',
    subtitle: 'Фильтрация по направлению, тегу и тексту вопроса.',
    allDirections: 'Все направления',
    allTags: 'Все теги',
    chooseDirectionFirst: 'Сначала выберите направление',
    searchPlaceholder: 'Поиск по вопросам',
    addQuestion: 'Добавить вопрос',
    editQuestion: 'Изменить',
    deleteQuestion: 'Удалить',
    directionsLoadError: 'Ошибка загрузки направлений',
    tagsLoadError: 'Ошибка загрузки тегов',
    questionsLoadError: 'Ошибка загрузки вопросов',
    savingError: 'Не удалось сохранить вопрос',
    loading: 'Загрузка...',
    nothingFound: 'Ничего не найдено',
    markdownPreview: 'Превью',
    markdownHint: 'Поле поддерживает Markdown.',
}

const parseNumberParam = (value: string | null) => {
    if (!value) return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
}

const getTagColorClass = (tagId: number) => 'home__question-tag_color_' + (tagId % 6)

const EMPTY_FORM: QuestionUpsertRequest = {
    directionId: 0,
    title: '',
    answer: '',
    tagIds: [],
}

export const HomePage = () => {
    const navigate = useNavigate()
    const { user } = useCurrentUser()
    const canEditContent = canManageContent(user?.role)
    const canDeleteContent = isAdminRole(user?.role)

    const [searchParams, setSearchParams] = useSearchParams()
    const directionId = useMemo(() => parseNumberParam(searchParams.get('directionId')), [searchParams])
    const tagId = useMemo(() => parseNumberParam(searchParams.get('tagId')), [searchParams])
    const search = searchParams.get('search') ?? ''

    const [directions, setDirections] = useState<DirectionResponse[]>([])
    const [tags, setTags] = useState<TagResponse[]>([])
    const [questions, setQuestions] = useState<QuestionResponse[]>([])
    const [searchInput, setSearchInput] = useState(search)
    const [isDirectionsLoading, setIsDirectionsLoading] = useState(false)
    const [isTagsLoading, setIsTagsLoading] = useState(false)
    const [isQuestionsLoading, setIsQuestionsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [modalOpen, setModalOpen] = useState(false)
    const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null)
    const [questionForm, setQuestionForm] = useState<QuestionUpsertRequest>(EMPTY_FORM)
    const [modalDirectionTags, setModalDirectionTags] = useState<TagResponse[]>([])
    const [isSavingQuestion, setIsSavingQuestion] = useState(false)
    const [isModalTagsLoading, setIsModalTagsLoading] = useState(false)

    useEffect(() => {
        setSearchInput(search)
    }, [search])

    useEffect(() => {
        setIsDirectionsLoading(true)
        QuestionsApi.getDirections()
            .then(setDirections)
            .catch((requestError) => setError(getApiErrorMessage(requestError, TEXT.directionsLoadError)))
            .finally(() => setIsDirectionsLoading(false))
    }, [])

    useEffect(() => {
        if (!directionId) {
            setTags([])
            return
        }

        setIsTagsLoading(true)
        QuestionsApi.getDirectionTags(directionId)
            .then(setTags)
            .catch((requestError) => setError(getApiErrorMessage(requestError, TEXT.tagsLoadError)))
            .finally(() => setIsTagsLoading(false))
    }, [directionId])

    useEffect(() => {
        setIsQuestionsLoading(true)
        setError(null)
        QuestionsApi.getQuestions({ directionId, tagId, search, page: 0, size: 50, sort: 'title,asc' })
            .then((page) => setQuestions(page.content))
            .catch((requestError) => setError(getApiErrorMessage(requestError, TEXT.questionsLoadError)))
            .finally(() => setIsQuestionsLoading(false))
    }, [directionId, tagId, search])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            const next = new URLSearchParams(searchParams)
            const trimmedSearch = searchInput.trim()
            const currentSearch = searchParams.get('search') ?? ''
            if (currentSearch === trimmedSearch) {
                return
            }
            if (trimmedSearch) {
                next.set('search', trimmedSearch)
            } else {
                next.delete('search')
            }
            setSearchParams(next, { replace: true })
        }, 400)

        return () => window.clearTimeout(timeoutId)
    }, [searchInput, searchParams, setSearchParams])

    useEffect(() => {
        if (!modalOpen) {
            return
        }

        const nextDirectionId = questionForm.directionId || directionId || directions[0]?.id || null
        if (!nextDirectionId) {
            setModalDirectionTags([])
            return
        }

        setIsModalTagsLoading(true)
        QuestionsApi.getDirectionTags(nextDirectionId)
            .then((nextTags) => {
                setModalDirectionTags(nextTags)
                setQuestionForm((current) => {
                    if (current.directionId !== nextDirectionId) {
                        return { ...current, directionId: nextDirectionId, tagIds: [] }
                    }
                    const nextTagIds = current.tagIds.filter((tagIdItem) => nextTags.some((tag) => tag.id === tagIdItem))
                    return { ...current, tagIds: nextTagIds }
                })
            })
            .catch((requestError) => setError(getApiErrorMessage(requestError, TEXT.tagsLoadError)))
            .finally(() => setIsModalTagsLoading(false))
    }, [directionId, directions, modalOpen, questionForm.directionId])

    const updateParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(searchParams)
        if (value) {
            next.set(key, value)
        } else {
            next.delete(key)
        }
        if (key === 'directionId') {
            next.delete('tagId')
        }
        setSearchParams(next)
    }

    const refreshQuestions = async () => {
        const page = await QuestionsApi.getQuestions({ directionId, tagId, search, page: 0, size: 50, sort: 'title,asc' })
        setQuestions(page.content)
    }

    const openCreateQuestion = () => {
        const firstDirectionId = directionId || directions[0]?.id || 0
        setEditingQuestionId(null)
        setQuestionForm({
            directionId: firstDirectionId,
            title: '',
            answer: '',
            tagIds: [],
        })
        setModalOpen(true)
    }

    const openEditQuestion = (question: QuestionResponse) => {
        setEditingQuestionId(question.id)
        setQuestionForm({
            directionId: question.direction.id,
            title: question.title,
            answer: question.answer,
            tagIds: question.tags.map((tag) => tag.id),
        })
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingQuestionId(null)
        setQuestionForm(EMPTY_FORM)
        setModalDirectionTags([])
        setIsModalTagsLoading(false)
    }

    const saveQuestion = async (event: FormEvent) => {
        event.preventDefault()
        if (!questionForm.directionId || !questionForm.title.trim() || !questionForm.answer.trim()) {
            return
        }

        setIsSavingQuestion(true)
        setError(null)
        try {
            const payload: QuestionUpsertRequest = {
                directionId: questionForm.directionId,
                title: questionForm.title.trim(),
                answer: questionForm.answer.trim(),
                tagIds: questionForm.tagIds,
            }

            if (editingQuestionId === null) {
                await AdminContentApi.createQuestion(payload)
            } else {
                await AdminContentApi.updateQuestion(editingQuestionId, payload)
            }

            await refreshQuestions()
            closeModal()
        } catch (requestError) {
            setError(getApiErrorMessage(requestError, TEXT.savingError))
        } finally {
            setIsSavingQuestion(false)
        }
    }

    const deleteQuestion = async (id: number) => {
        if (!window.confirm('Удалить вопрос?')) {
            return
        }
        setIsQuestionsLoading(true)
        setError(null)
        try {
            await AdminContentApi.deleteQuestion(id)
            await refreshQuestions()
        } catch (requestError) {
            setError(getApiErrorMessage(requestError, TEXT.questionsLoadError))
        } finally {
            setIsQuestionsLoading(false)
        }
    }

    return (
        <>
            <Header />
            <main className="home">
                <section className="home__top">
                    <div>
                        <h1 className="home__title">{TEXT.title}</h1>
                        <p className="home__subtitle">{TEXT.subtitle}</p>
                    </div>
                </section>

                <section className="home__filters">
                    <select
                        className="home__select"
                        value={directionId ?? ''}
                        onChange={(event) => updateParam('directionId', event.target.value || null)}
                        disabled={isDirectionsLoading}
                    >
                        <option value="">{TEXT.allDirections}</option>
                        {directions.map((direction) => (
                            <option key={direction.id} value={direction.id}>{direction.name}</option>
                        ))}
                    </select>

                    <input
                        className="home__search"
                        type="search"
                        placeholder={TEXT.searchPlaceholder}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />

                    <select
                        className="home__select"
                        value={tagId ?? ''}
                        onChange={(event) => updateParam('tagId', event.target.value || null)}
                        disabled={!directionId || isTagsLoading}
                    >
                        <option value="">{directionId ? TEXT.allTags : TEXT.chooseDirectionFirst}</option>
                        {tags.map((tag) => (
                            <option key={tag.id} value={tag.id}>{tag.name}</option>
                        ))}
                    </select>

                    <div className="home__filterActions">
                        {canEditContent ? (
                            <button className="home__addButton" type="button" onClick={openCreateQuestion} disabled={!directions.length}>
                                {TEXT.addQuestion}
                            </button>
                        ) : null}
                    </div>
                </section>

                {error ? <p className="home__error">{error}</p> : null}
                {isQuestionsLoading ? <p className="home__empty">{TEXT.loading}</p> : null}

                {!isQuestionsLoading && !error ? (
                    <section className="home__questions">
                        {questions.map((question) => (
                            <article
                                key={question.id}
                                className="home__question"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate('/app/questions/' + question.id)}
                                onKeyDown={(event) => (event.key === 'Enter' ? navigate('/app/questions/' + question.id) : null)}
                            >
                                <div className="home__questionBody">
                                    <h2 className="home__question-title">{question.title}</h2>
                                    <div className="home__question-tags">
                                        {question.tags.map((tag) => (
                                            <span key={tag.id} className={'home__question-tag ' + getTagColorClass(tag.id)}>{tag.name}</span>
                                        ))}
                                    </div>
                                </div>

                                {canEditContent ? (
                                    <div className="home__question-actions" onClick={(event) => event.stopPropagation()}>
                                        <button className="home__question-action" type="button" onClick={() => openEditQuestion(question)}>
                                            {TEXT.editQuestion}
                                        </button>
                                        {canDeleteContent ? (
                                            <button className="home__question-action home__question-action_danger" type="button" onClick={() => void deleteQuestion(question.id)}>
                                                {TEXT.deleteQuestion}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}
                            </article>
                        ))}

                        {!questions.length ? <p className="home__empty">{TEXT.nothingFound}</p> : null}
                    </section>
                ) : null}
            </main>

            {modalOpen ? (
                <section className="home-modal">
                    <div className="home-modal__backdrop" onClick={closeModal} />
                    <div className="home-modal__dialog">
                        <button className="home-modal__close" type="button" onClick={closeModal}>×</button>
                        <h2 className="home-modal__title">{editingQuestionId === null ? TEXT.addQuestion : TEXT.editQuestion}</h2>
                        <form className="home-modal__form" onSubmit={saveQuestion}>
                            <div className="home-modal__grid">
                                <div className="home-modal__column">
                                    <label className="home-modal__label">
                                        Направление
                                        <select
                                            className="home-modal__input"
                                            value={questionForm.directionId || ''}
                                            onChange={(event) => setQuestionForm((current) => ({ ...current, directionId: Number(event.target.value), tagIds: [] }))}
                                        >
                                            <option value="">Выберите направление</option>
                                            {directions.map((direction) => (
                                                <option key={direction.id} value={direction.id}>{direction.name}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="home-modal__label">
                                        Заголовок
                                        <input
                                            className="home-modal__input"
                                            value={questionForm.title}
                                            onChange={(event) => setQuestionForm((current) => ({ ...current, title: event.target.value }))}
                                            placeholder="Например: Чем отличается dependency и dependencyManagement?"
                                        />
                                    </label>

                                    <label className="home-modal__label">
                                        Ответ в Markdown
                                        <textarea
                                            className="home-modal__textarea"
                                            value={questionForm.answer}
                                            onChange={(event) => setQuestionForm((current) => ({ ...current, answer: event.target.value }))}
                                            placeholder="Можно использовать списки, заголовки, кодовые блоки и ссылки"
                                            rows={18}
                                        />
                                    </label>

                                    <div className="home-modal__hint">{TEXT.markdownHint}</div>
                                </div>

                                <div className="home-modal__column home-modal__previewColumn">
                                    <div className="home-modal__previewHeader">{TEXT.markdownPreview}</div>
                                    <div className="home-modal__preview">
                                        {renderMarkdown(questionForm.answer || ' ')}
                                    </div>
                                </div>
                            </div>

                            <div className="home-modal__tagsBlock">
                                <div className="home-modal__tagsTitle">Теги</div>
                                {isModalTagsLoading ? <p className="home__empty">{TEXT.loading}</p> : null}
                                <div className="home-modal__tags">
                                    {modalDirectionTags.map((tag) => {
                                        const checked = questionForm.tagIds.includes(tag.id)
                                        return (
                                            <label key={tag.id} className={'home-modal__tag ' + (checked ? 'home-modal__tag_active' : '')}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => setQuestionForm((current) => {
                                                        const nextTagIds = checked
                                                            ? current.tagIds.filter((tagIdValue) => tagIdValue !== tag.id)
                                                            : [...current.tagIds, tag.id].sort((left, right) => left - right)
                                                        return { ...current, tagIds: nextTagIds }
                                                    })}
                                                />
                                                <span>{tag.name}</span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>

                            <button className="home-modal__submit" type="submit" disabled={isSavingQuestion}>
                                {isSavingQuestion ? TEXT.loading : 'Сохранить'}
                            </button>
                        </form>
                    </div>
                </section>
            ) : null}
        </>
    )
}
